/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import './NavBarCSS.css';

// JavaScript plugin that hides or shows a component based on your scroll
import Headroom from "headroom.js";
// reactstrap components
import {
  Collapse,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
  Media,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Card,
} from "reactstrap";
import { useUserAuth } from "../../context/UserContext";
import { useNavigate } from "react-router";

import { logoutUser } from "../../services/userService"
import { useTranslation, Trans } from "react-i18next";
import { resolveAvatar } from "../../constants/avatars";
import { isPrivilegedRole, canAccessResources } from "../../utils/roles";
import { COURSES_OPEN } from "../../config/courses";

const DemoNavbar = () => {

  const [collapseClasses, setCollapseClasses] = useState("");
  const [collapseOpen, setCollapseOpen] = useState(false);
  const { logOut, user } = useUserAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // "en-US" → "en"; drives the EN/ES code on the language toggle.
  const lang = (i18n.language || "en").slice(0, 2);

  // Marks the link for the section the visitor is in, so /studio/abc still
  // lights up "Studio". Prefix match on a whole segment: /courses2 does not
  // count as /courses.
  const linkProps = (to) => {
    const active = pathname === to || pathname.startsWith(`${to}/`);
    return {
      className: `demoNavBar__link${active ? " is-active" : ""}`,
      "aria-current": active ? "page" : undefined,
    };
  };

  const handleLogout = async () => {
    try {
      // BEFORE REMOVING BELLOW FIX USERONTEXT SETUP
      // doing code bellow in user contex
      // await logoutUser();

      await logOut();
      navigate("/");
    } catch (error) {
      console.log(error.message);
    }
  };

  // useEffect(() => {
  //   const headroom = new Headroom(document.getElementById("navbar-main"));
  //   // initialize
  //   headroom.init();
  // }, []);

  const onExiting = () => {
    setCollapseClasses("collapsing-out");
  };

  const onExited = () => {
    setCollapseClasses("");
  };

  return (
    <>
      <header className="header-global ">
        {/* No `bg-default`: the bar paints its own night fill in
            NavBarCSS.css, and pages that float it over a hero still override
            that with their `body.<page>-active #navbar-main` rules. */}
        <Navbar
          className="navbar-dark demoNavBar"
          expand="lg"
          id="navbar-main"
        >

          <Container>
            {/* The wordmark is aria-hidden because the logo's alt already
                names the link; reading both would say "CodeNec Codenec". */}
            <NavbarBrand className="demoNavBar__brand mr-lg-5" to="/" tag={Link}>
              <img
                alt="CodeNec"
                className="demoNavBar__brand-img"
                src={require("assets/img/brand/CodenecLogoNoLettersTransparent.png")}
              />
              <span className="demoNavBar__wordmark" aria-hidden="true">Codenec</span>
            </NavbarBrand>

            {/* Right-hand cluster that stays visible in the top bar on every
                screen size (it does NOT collapse): the AI Assistant dock and
                the profile menu for signed-in users, plus the hamburger toggle.
                Everything else folds into the collapse below. `order-lg-last`
                pushes this group to the far right on desktop. */}
            <div className="demoNavBar__actions d-flex align-items-center order-lg-last">
              {/* 🤖 AI Assistant dock slot — kept outside the collapse so the
                  floating robot can always snap to it, even on mobile. Staff
                  only: AiAssistantGate renders the orb for admins and testers
                  alone, and a socket with no orb to fill it is a bare circle. */}
              {isPrivilegedRole(user?.role) && (
                <div className="ai-robot-nav-item">
                  <div id="ai-robot-dock" />
                </div>
              )}

              {user && (
                <UncontrolledDropdown nav>
                  <DropdownToggle nav className="pr-0">
                    <img
                      alt={t("nav.Profile")}
                      className="rounded-circle demoNavBar__profile-img"
                      src={resolveAvatar(user?.avatarId)}
                      style={{ objectFit: 'contain', backgroundColor: '#f6f9fc' }}
                    />
                  </DropdownToggle>

                  <DropdownMenu right>
                    <DropdownItem to="/profile-page" tag={Link}>
                      {t("nav.Profile")}
                    </DropdownItem>
                    <DropdownItem onClick={handleLogout} tag={Link}>
                      {t("nav.Logout")}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}

              <button
                className="navbar-toggler ml-2"
                type="button"
                aria-label={t("nav.openMenu")}
                aria-expanded={collapseOpen}
                onClick={() => setCollapseOpen((open) => !open)}
              >
                <span className="navbar-toggler-icon" />
              </button>
            </div>

            <Collapse
              isOpen={collapseOpen}
              navbar
              className={collapseClasses}
              onExiting={onExiting}
              onExited={onExited}
            >
              <div className="navbar-collapse-header ">
                <Row>
                  <Col className="collapse-brand" xs="6">
                    <Link to="/" className="demoNavBar__brand">
                      <img
                        alt="CodeNec"
                        className="demoNavBar__brand-img"
                        src={require("assets/img/brand/CodenecLogoNoLettersTransparent.png")}
                      />
                      <span className="demoNavBar__wordmark" aria-hidden="true">Codenec</span>
                    </Link>
                  </Col>
                  <Col className="collapse-close" xs="6">
                    <button
                      className="navbar-toggler "
                      type="button"
                      aria-label={t("nav.closeMenu")}
                      onClick={() => setCollapseOpen(false)}
                    >
                      <span />
                      <span />
                    </button>
                  </Col>
                </Row>
              </div>

              {/* Primary navigation (left on desktop, stacked in the hamburger
                  menu on mobile). */}
              <Nav className="navbar-nav-hover align-items-lg-center" navbar>

                {user?.role === "admin" && (

                  <UncontrolledDropdown nav>
                    <DropdownToggle nav>
                      <i className="ni ni-ui-04 d-lg-none mr-1" />
                      <span className="nav-link-inner--text">{t("nav.Components")}</span>
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-xl">
                      <div className="dropdown-menu-inner">
                        <Media
                          className="d-flex align-items-center"
                          href="assets/img/brand/TempProfileAvatar1.PNG"
                          target="_blank"
                        >
                          <div className="icon icon-shape bg-gradient-primary rounded-circle text-white">
                            <i className="ni ni-spaceship" />
                          </div>
                          <Media body className="ml-3">
                            <h6 className="heading text-primary mb-md-1">
                              Getting started
                            </h6>
                            <p className="description d-none d-md-inline-block mb-0">
                              Learn how to use Argon compiling Scss, change brand
                              colors and more.
                            </p>
                          </Media>
                        </Media>
                        <Media
                          className="d-flex align-items-center"
                          href="https://demos.creative-tim.com/argon-design-system-react/#/documentation/colors?ref=adsr-navbar"
                          target="_blank"
                        >
                          <div className="icon icon-shape bg-gradient-success rounded-circle text-white">
                            <i className="ni ni-palette" />
                          </div>
                          <Media body className="ml-3">
                            <h6 className="heading text-primary mb-md-1">
                              Foundation
                            </h6>
                            <p className="description d-none d-md-inline-block mb-0">
                              Learn more about colors, typography, icons and the
                              grid system we used for Argon.
                            </p>
                          </Media>
                        </Media>
                        <Media
                          className="d-flex align-items-center"
                          href="https://demos.creative-tim.com/argon-design-system-react/#/documentation/alert?ref=adsr-navbar"
                          target="_blank"
                        >
                          <div className="icon icon-shape bg-gradient-warning rounded-circle text-white">
                            <i className="ni ni-ui-04" />
                          </div>
                          <Media body className="ml-3">
                            <h5 className="heading text-warning mb-md-1">
                              Components
                            </h5>
                            <p className="description d-none d-md-inline-block mb-0">
                              Browse our 50 beautiful handcrafted components
                              offered in the Free version.
                            </p>
                          </Media>
                        </Media>
                      </div>
                    </DropdownMenu>
                  </UncontrolledDropdown>

                )}

                {user?.role === "admin" && (
                  <UncontrolledDropdown nav>
                    <DropdownToggle nav>
                      <i className="ni ni-collection d-lg-none mr-1" />
                      <span className="nav-link-inner--text">{t("nav.Examples")}</span>
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem to="/index" tag={Link}>
                        Index Page
                      </DropdownItem>
                      <DropdownItem to="/ai" tag={Link}>
                        AI
                      </DropdownItem>

                    </DropdownMenu>
                  </UncontrolledDropdown>
                )}

                {/* Admin menu */}
                {user?.role === "admin" && (
                  <UncontrolledDropdown nav>
                    <DropdownToggle nav>
                      <i className="ni ni-collection d-lg-none mr-1" />
                      <span className="nav-link-inner--text">{t("nav.Admin")}</span>
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem to="/add-lesson" tag={Link}>
                        Add Lesson
                      </DropdownItem>
                      <DropdownItem to="/add-pp" tag={Link}>
                        Add Practice Problem
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                )}

                {/* Hidden while the courses are closed — CoursesAreaGate would
                    bounce anyone who clicked it. Admins and testers keep the
                    link because they keep access. See config/courses.js. */}
                {user && (COURSES_OPEN || isPrivilegedRole(user?.role)) && (
                  <NavItem to="/courses" tag={Link} {...linkProps("/courses")}>
                    {t("nav.Courses")}
                  </NavItem>
                )}

                {/* Client resource library. Clients only — plus admins and
                    testers, who canAccessResources lets through so the page can
                    be checked without switching accounts. A plain "user" (a
                    course student) never sees this. */}
                {canAccessResources(user?.role) && (
                  <NavItem to="/resources" tag={Link} {...linkProps("/resources")}>
                    {t("nav.resources")}
                  </NavItem>
                )}

                {/* Codenec Studio, for invited clients only (the same flag
                    the Studio service checks). */}
                {user?.studioEnabled === true && (
                  <NavItem to="/studio" tag={Link} {...linkProps("/studio")}>
                    {t("nav.studio")}
                  </NavItem>
                )}

                {user?.role === "admin" && (
                  <NavItem to="/quiz" tag={Link} {...linkProps("/quiz")}>
                    Testing
                  </NavItem>
                )}

              </Nav>

              {/* Secondary navigation: language + auth actions. Pushed to the
                  right on desktop (`ml-lg-auto`), stacked under the primary nav
                  inside the hamburger menu on mobile. */}
              <Nav className="navbar-nav-hover align-items-lg-center ml-lg-auto" navbar>

                {/* Compact on desktop (globe + EN/ES); the "Language" label
                    stays for screen readers and shows in the mobile sheet. */}
                <UncontrolledDropdown nav className="demoNavBar__lang">
                  <DropdownToggle nav className="demoNavBar__lang-toggle">
                    <svg
                      className="demoNavBar__lang-globe"
                      viewBox="0 0 16 16"
                      width="16"
                      height="16"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle cx="8" cy="8" r="6.25" />
                      <path d="M1.75 8h12.5M8 1.75c1.65 1.7 2.5 3.8 2.5 6.25S9.65 12.55 8 14.25M8 1.75C6.35 3.45 5.5 5.55 5.5 8s.85 4.55 2.5 6.25" />
                    </svg>
                    <span className="nav-link-inner--text demoNavBar__lang-label">
                      {t("nav.language")}
                    </span>
                    <span className="demoNavBar__lang-code">{lang.toUpperCase()}</span>
                    <svg
                      className="demoNavBar__chevron"
                      viewBox="0 0 10 6"
                      width="10"
                      height="6"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M1 1l4 4 4-4" />
                    </svg>
                  </DropdownToggle>

                  <DropdownMenu right>
                    <DropdownItem
                      className={lang === "es" ? "is-current" : undefined}
                      lang="es"
                      onClick={() => { i18n.changeLanguage("es"); setCollapseOpen(false); }}
                      tag={Link}
                    >
                      Español
                    </DropdownItem>
                    <DropdownItem
                      className={lang === "en" ? "is-current" : undefined}
                      lang="en"
                      onClick={() => { i18n.changeLanguage("en"); setCollapseOpen(false); }}
                      tag={Link}
                    >
                      English
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>

                {/* One primary action: "Create account". "Log in" stays quiet
                    beside it. Plain buttons rather than reactstrap's <Button>,
                    so none of Argon's .btn uppercase/shadow/lift leaks in. */}
                {!user && (
                  <NavItem className="demoNavBar__auth">
                    <button
                      className="demoNavBar__btn demoNavBar__btn--ghost"
                      type="button"
                      onClick={() => navigate("/login-page")}
                    >
                      {t("nav.login")}
                    </button>
                  </NavItem>
                )}

                {!user && (
                  <NavItem className="demoNavBar__auth">
                    <button
                      className="demoNavBar__btn demoNavBar__btn--primary"
                      type="button"
                      onClick={() => navigate("/register-page")}
                    >
                      {t("nav.register")}
                    </button>
                  </NavItem>
                )}

              </Nav>

            </Collapse>

          </Container>
        </Navbar>
      </header>
    </>
  );
};

export default DemoNavbar;
